import { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../api/client";
import type { ErrorLog, WSMessage, AppSettings, FocusTarget, SessionMode } from "../types";
import { useWebSocket } from "../hooks/useWebSocket";
import { useAudioWorklet } from "../hooks/useAudioWorklet";
import { useAudioPlayback } from "../hooks/useAudioPlayback";
import { useVisualiser } from "../hooks/useVisualiser";
import Transcript, { type BubbleEntry } from "../components/conversation/Transcript";
import AudioControls from "../components/conversation/AudioControls";
import SessionOverlay from "../components/conversation/SessionOverlay";
import TutorPanel, { TutorStrip, Avatar } from "../components/conversation/TutorPanel";
import RhythmTracker from "../components/RhythmTracker";
import MobileFallback from "../components/MobileFallback";
import { useAppOutletContext } from "../App";
import { track, pauseSessionRecording, resumeSessionRecording } from "../lib/analytics";

type Phase = "idle" | "connected" | "ended";

interface SessionEnd {
  sessionId: number;
  topic: string | null;
  summary: string | null;
}

// AUTO mode is disabled on touch devices: phone loudspeaker + mic share air, and
// iOS echo cancellation is not reliable enough for silence-detection turn-taking
// (Elena's own reply can open/close phantom student turns). Manual tap-to-talk
// only there; desktop keeps the toggle.
const IS_TOUCH_DEVICE = typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches;

// Practice sessions are desktop-only for now: mobile browsers can't reliably
// run the voice turn-taking loop (speaker/mic echo, aggressive tab suspension,
// unreliable audio capture). Gate the whole session start on a non-touch pointer
// with a wide enough viewport for the transcript + live-feedback layout.
// The admin-only `mobile_sessions_enabled` setting lifts the gate (see below).
const IS_SMALL_SCREEN = typeof window !== "undefined" &&
  (window.matchMedia?.("(pointer: coarse)").matches || window.matchMedia?.("(max-width: 820px)").matches);

let bubbleCounter = 0;
function newBubble(who: "elena" | "student", text: string): BubbleEntry {
  return { id: `b-${++bubbleCounter}`, who, text };
}

// Minimum time the processing dots stay on screen once shown. A cascaded reply can
// arrive as a single batch (student_transcript + audio + text + elena_done together),
// which would otherwise flip the dots true→false inside one 0.3s fade-in so they never
// visually appear. This floor guarantees they paint after the student finishes a turn.
const MIN_DOTS_MS = 650;

function FocusTargets({ targets, selected, onSelect }: {
  targets: FocusTarget[];
  selected: string | undefined;
  onSelect: (key: string) => void;
}) {
  return (
    <div className="card w-full px-5 py-4 mb-6 text-left">
      <h4 className="section-label mb-1">Pick one to practise</h4>
      <p className="text-xs text-text-tertiary mb-3 leading-relaxed">
        Elena drills just this one: she points out every slip and tells you when you get it right.
      </p>
      <div role="radiogroup" className="flex flex-col gap-1">
        {targets.map((t) => {
          const active = t.key === selected;
          return (
            <button
              key={t.key}
              role="radio"
              aria-checked={active}
              onClick={() => onSelect(t.key)}
              className={`flex items-start gap-3 rounded-[10px] px-3 py-2 text-left transition-colors duration-150 ${
                active ? "bg-success/10 ring-1 ring-success/40" : "hover:bg-surface2/60"
              }`}
            >
              <span className={`mt-1 w-3 h-3 rounded-full border-2 shrink-0 ${active ? "border-success bg-success" : "border-border"}`} />
              <span className="min-w-0 text-sm">
                <span className="text-ink">{t.label}</span>
                <span className="text-text-tertiary"> · {t.example_right}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function IdleHero({ onStart, onShowFocus, settings, connecting, focusTargets, preferFocus, initialFocusKey }: {
  onStart: (mode: SessionMode, focusKey?: string) => void;
  onShowFocus: () => void;
  settings: AppSettings | null;
  connecting: boolean;
  focusTargets: FocusTarget[];
  preferFocus: boolean;
  initialFocusKey: string | null;
}) {
  // Blocked until settings load, so a slow fetch never lets a mobile session start.
  const blocked = IS_SMALL_SCREEN && !settings?.mobile_sessions_enabled;
  // The picked focus target; defaults to the dashboard's pick (?target=) or the top one.
  const [picked, setPicked] = useState<string | null>(null);
  const selectedKey = (
    focusTargets.find((t) => t.key === picked)
    ?? focusTargets.find((t) => t.key === initialFocusKey)
    ?? focusTargets[0]
  )?.key;
  const showPicker = preferFocus && focusTargets.length > 0;

  // The dead-end rate is the number that decides whether mobile-heavy channels
  // (Instagram, TikTok) are worth pursuing at all, so it has to be measured.
  // Gated on `settings` so the pre-load blocked state doesn't fire a false positive.
  const blockedTracked = useRef(false);
  useEffect(() => {
    if (!settings || !blocked || blockedTracked.current) return;
    blockedTracked.current = true;
    track("mobile_blocked", {
      // The marketing hero fires this too (web/src/mobile.jsx); `source_page`
      // keeps the two surfaces distinguishable inside the one funnel.
      source_page: "app_conversation",
      screen_width: window.innerWidth,
      pointer_type: window.matchMedia?.("(pointer: coarse)").matches ? "coarse" : "fine",
    });
  }, [settings, blocked]);

  return (
    <div className="flex-1 flex flex-col items-center px-6 text-center overflow-y-auto">
      <div className="animate-rise flex flex-col items-center max-w-md py-10 my-auto">
        <div className="lg:hidden mb-7">
          <Avatar amp={0} size={88} />
        </div>
        <h1 className="font-display mb-4">
          {blocked ? <>Non da qui<span className="text-accent">.</span></> : <>Parliamo<span className="text-accent">.</span></>}
        </h1>
        <p className="text-text-secondary text-[0.9375rem] leading-relaxed mb-8 max-w-sm">
          {blocked
            ? "Speaking sessions run on a laptop or desktop. Here's what they look like, and how to pick this up later."
            : "Start a session and have a real conversation in Italian. Elena listens, corrects quietly, and remembers how you're doing."}
        </p>
        {/* A disabled button under a warning was a dead end; the fallback offers a
            demo, a link for later, and what still works here. */}
        {blocked ? (
          <MobileFallback sourcePage="app_conversation" />
        ) : (
          <>
            {/* Focus session: Elena drills ONE of the top mistakes. Arriving from the
                dashboard (?focus=1, optionally &target=<key>) opens the picker. */}
            {showPicker && <FocusTargets targets={focusTargets} selected={selectedKey} onSelect={setPicked} />}
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                className="btn-primary px-8 py-3 text-[0.9375rem] disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={() => (showPicker ? onStart("focus", selectedKey) : onStart("normal"))}
                disabled={connecting}
              >
                {connecting ? "Connecting…" : showPicker ? "Start focus session" : "Start session"}
              </button>
              {focusTargets.length > 0 && (
                <button
                  className="btn-quiet px-6 py-3 text-[0.9375rem] disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={() => (showPicker ? onStart("normal") : onShowFocus())}
                  disabled={connecting}
                >
                  {showPicker ? "Normal session" : "Focus on a mistake"}
                </button>
              )}
            </div>
          </>
        )}
        {settings && !blocked && (
          <p className="mt-6 text-[0.6875rem] uppercase tracking-[0.16em] text-text-tertiary font-medium">
            Level {settings.tutor_level}
          </p>
        )}
        {!blocked && <RhythmTracker />}
      </div>
    </div>
  );
}

export default function ConversationPage() {
  const [phase, setPhase] = useState<Phase>("idle");
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [transcript, setTranscript] = useState<BubbleEntry[]>([]);
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [sessionEnd, setSessionEnd] = useState<SessionEnd | null>(null);
  const [ending, setEnding] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [paused, setPaused] = useState(false);
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [toastError, setToastError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [focusTargets, setFocusTargets] = useState<FocusTarget[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const preferFocus = searchParams.get("focus") === "1";
  // The target being drilled in the live focus session (null in normal sessions).
  const [focusLabel, setFocusLabel] = useState<string | null>(null);
  const navigate = useNavigate();
  const { setSessionActive } = useAppOutletContext();

  // Hide the mobile tab bar while the session is live; restore on unmount.
  useEffect(() => {
    setSessionActive(phase === "connected");
    return () => setSessionActive(false);
  }, [phase, setSessionActive]);

  const startTimeRef = useRef<number | null>(null);
  // The server's ordinal for this session (their 1st, 2nd, …), tagged onto
  // `session_started` — see api/session.session_number.
  const sessionNumberRef = useRef<number>(0);
  // Mode the server actually used (a focus request with no targets runs normal).
  const sessionModeRef = useRef<SessionMode>("normal");
  // Wall-clock time the student paused; the elapsed counter freezes while set and
  // startTimeRef is shifted forward by the paused span on resume.
  const pauseStartRef = useRef<number | null>(null);
  const pausedRef = useRef(false);
  pausedRef.current = paused;
  // Safety net: if the server never confirms the end, close the UI locally.
  const endTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [vadMode, setVadMode] = useState<"manual" | "auto">("manual");
  const [elenaThinking, setElenaThinking] = useState(false); // Elena composing her reply
  const [transcribing, setTranscribing] = useState(false);   // student's speech being transcribed (STT)

  const visualiser = useVisualiser(16);

  // Refs to read latest state inside callbacks defined before state is available
  const vadModeRef = useRef(vadMode);
  vadModeRef.current = vadMode;
  const phaseRef = useRef<Phase>("idle");
  phaseRef.current = phase;
  const endingRef = useRef(false);
  endingRef.current = ending;
  // Latest recording state, readable inside the auto-mode watchdog interval.
  const isRecordingRef = useRef(false);

  // sendRef breaks the circular dependency between useAudioWorklet and useWebSocket
  const sendRef = useRef<(msg: object) => void>(() => { /* noop until connected */ });
  // stopRef / startRef break the same dependency for silence callbacks → audioWorklet
  const stopRef = useRef<() => void>(() => {});
  const startRef = useRef<() => Promise<void>>(() => Promise.resolve());
  // Bridge to the worklet hook's mic-rate ref (hook is created below); reported
  // in turn_start so server logs can confirm the device's real capture rate.
  const micRateRef = useRef<{ current: number | null } | null>(null);

  // Auto-mode turn gating: mic only opens when BOTH playback is done AND elena_done arrived.
  const elenaDoneRef = useRef(false);
  const playbackDonePendingRef = useRef(false);

  // Bubble turn boundaries. A "text" chunk may append to Elena's current bubble
  // only while her turn is still open; elena_done / interrupted close it so her
  // next reply always starts a fresh bubble — even when no student transcript
  // arrived in between (Gemini's input transcription can skip short utterances,
  // which used to fuse every reply into one endless bubble). Same for the student:
  // a new turn_start closes their bubble so each voice turn renders separately.
  const elenaBubbleOpenRef = useRef(false);
  const studentBubbleOpenRef = useRef(false);

  // Processing-dots lifecycle with a minimum on-screen time (see MIN_DOTS_MS).
  const thinkingSinceRef = useRef<number | null>(null);
  const thinkingClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Show the student-side "transcribing" dots (STT in progress, before the
  // student's words are known). Mutually exclusive with Elena's dots.
  const showTranscribing = useCallback(() => {
    if (thinkingClearTimerRef.current) { clearTimeout(thinkingClearTimerRef.current); thinkingClearTimerRef.current = null; }
    setElenaThinking(false);
    setTranscribing(true);
  }, []);

  // Switch to Elena's dots (she's composing her reply). Clears the student dots.
  const showThinking = useCallback(() => {
    if (thinkingClearTimerRef.current) { clearTimeout(thinkingClearTimerRef.current); thinkingClearTimerRef.current = null; }
    setTranscribing(false);
    thinkingSinceRef.current = performance.now();
    setElenaThinking(true);
  }, []);

  // Hide the dots, but never before Elena's have been visible for MIN_DOTS_MS.
  const hideThinking = useCallback(() => {
    setTranscribing(false);
    if (thinkingClearTimerRef.current) return; // a delayed clear is already pending
    const since = thinkingSinceRef.current;
    const elapsed = since == null ? MIN_DOTS_MS : performance.now() - since;
    if (elapsed >= MIN_DOTS_MS) {
      setElenaThinking(false);
    } else {
      thinkingClearTimerRef.current = setTimeout(() => {
        thinkingClearTimerRef.current = null;
        setElenaThinking(false);
      }, MIN_DOTS_MS - elapsed);
    }
  }, []);

  // Immediate hide of both for teardown / barge-in paths.
  const forceHideThinking = useCallback(() => {
    if (thinkingClearTimerRef.current) { clearTimeout(thinkingClearTimerRef.current); thinkingClearTimerRef.current = null; }
    setTranscribing(false);
    setElenaThinking(false);
  }, []);

  const autoStartMic = useCallback(() => {
    if (pausedRef.current) return; // never re-arm the mic while paused
    elenaDoneRef.current = false;
    playbackDonePendingRef.current = false;
    elenaBubbleOpenRef.current = false;
    studentBubbleOpenRef.current = false;
    sendRef.current({ type: "turn_start", mic_rate: micRateRef.current?.current ?? null });
    startRef.current().catch(console.error);
  }, []);

  const handleSilence = useCallback(() => {
    stopRef.current();
    // A finished student turn is a boundary for Elena's bubble even when her
    // previous turn never got an elena_done (Gemini can leave a turn open for
    // minutes and answer successive turns inside it — session 42): without this,
    // every one of those replies fuses into the same bubble.
    elenaBubbleOpenRef.current = false;
    // The student just spoke, so Elena OWES a reply: the elena_done from her
    // PREVIOUS turn is stale and must not re-arm the mic. Without this the
    // watchdog re-opened the mic the instant the turn ended (session 46,
    // turn_end and turn_start in the same second), Elena's incoming reply
    // played into the open mic, and on a phone loudspeaker the echo crossed the
    // voicing gate — a phantom student turn that barged her in and made her
    // answer her own question in a loop. The mic re-arms only on the elena_done
    // of the reply to THIS turn (or the server's empty-turn elena_done).
    elenaDoneRef.current = false;
    sendRef.current({ type: "turn_end" });
    showTranscribing();
  }, [showTranscribing]);

  const handlePlaybackDone = useCallback(() => {
    if (vadModeRef.current !== "auto" || phaseRef.current !== "connected" || endingRef.current) return;
    if (!elenaDoneRef.current) {
      playbackDonePendingRef.current = true; // wait for elena_done to confirm turn is complete
      return;
    }
    autoStartMic();
  }, [autoStartMic]);

  const audioPlayback = useAudioPlayback(visualiser.update, handlePlaybackDone);
  const audioWorklet = useAudioWorklet(
    (b64) => sendRef.current({ type: "audio", data: b64 }),
    {
      onSilence: vadMode === "auto" ? handleSilence : undefined,
      onTimeout: vadMode === "auto" ? handleSilence : undefined,
      silenceMs: 1500,
      preSpeechTimeoutMs: 10_000,
      maxTurnMs: 60_000,
      gateSpeech: vadMode === "auto",
    },
  );
  stopRef.current = audioWorklet.stop;
  startRef.current = audioWorklet.start;
  micRateRef.current = audioWorklet.micRateRef;
  isRecordingRef.current = audioWorklet.isRecording;
  // Stable getter for playback state, readable inside the watchdog interval.
  const isPlayingRef = useRef<() => boolean>(() => false);
  isPlayingRef.current = audioPlayback.isPlaying;

  // Auto-mode safety net: the mic re-arms by joining two edge-triggered signals
  // (elena_done + playback-done). If either edge is missed or a flag is reset out
  // of order, that join can stall and the mic never reopens after Elena speaks.
  // This watchdog re-checks the steady state and force-opens the mic when Elena's
  // turn is complete, audio has drained, and nothing else is happening. It is
  // idempotent — autoStartMic clears elenaDoneRef first, so it never double-fires.
  useEffect(() => {
    if (phase !== "connected" || vadMode !== "auto") return;
    const id = setInterval(() => {
      if (
        phaseRef.current === "connected" &&
        vadModeRef.current === "auto" &&
        !endingRef.current &&
        elenaDoneRef.current &&
        !isRecordingRef.current &&
        !isPlayingRef.current()
      ) {
        autoStartMic();
      }
    }, 500);
    return () => clearInterval(id);
  }, [phase, vadMode, autoStartMic]);

  // The transcript on this page is the student's own speech and Elena's replies —
  // never something to ship to a replay service — and the page is long-lived
  // enough that recording it dominates the replay quota. Off for the whole page,
  // back on when they navigate away.
  useEffect(() => {
    pauseSessionRecording();
    return () => resumeSessionRecording();
  }, []);

  // Top mistakes for the idle screen's focus option; refreshed each time the
  // page returns to idle, since a finished session can change the list.
  useEffect(() => {
    if (phase !== "idle") return;
    api.getFocus().then((f) => setFocusTargets(f.top)).catch(() => setFocusTargets([]));
  }, [phase]);

  useEffect(() => {
    api.getSettings().then((s) => {
      setSettings(s);
      setVadMode(IS_TOUCH_DEVICE ? "manual" : (s.mic_mode ?? "manual"));
    }).catch(() => { /* panel just omits the chips */ });
  }, []);

  const handleMessage = useCallback((msg: WSMessage) => {
    switch (msg.type) {
      case "audio":
        // While paused, drop Elena's voice frames so she stays silent. Any audio
        // still in flight from before the pause is discarded rather than queued.
        if (pausedRef.current) break;
        elenaDoneRef.current = false;
        playbackDonePendingRef.current = false;
        audioPlayback.enqueue(msg.data);
        break;
      case "text":
        setTranscript((prev) => {
          const last = prev[prev.length - 1];
          if (elenaBubbleOpenRef.current && last && last.who === "elena") {
            const updated = { ...last, text: last.text + " " + msg.content };
            return [...prev.slice(0, -1), updated];
          }
          return [...prev, newBubble("elena", msg.content)];
        });
        elenaBubbleOpenRef.current = true;
        studentBubbleOpenRef.current = false;
        break;
      case "student_transcript":
        // Transcription is in: the student's words are now written, so drop the
        // student-side dots and switch to Elena's dots while she composes her reply.
        showThinking();
        setTranscript((prev) => {
          const last = prev[prev.length - 1];
          if (studentBubbleOpenRef.current && last && last.who === "student") {
            const updated = { ...last, text: last.text + " " + msg.content };
            return [...prev.slice(0, -1), updated];
          }
          return [...prev, newBubble("student", msg.content)];
        });
        studentBubbleOpenRef.current = true;
        elenaBubbleOpenRef.current = false;
        break;
      case "interrupted":
        forceHideThinking();
        elenaBubbleOpenRef.current = false;
        audioPlayback.abort();
        break;
      case "elena_done":
        hideThinking();
        elenaDoneRef.current = true;
        elenaBubbleOpenRef.current = false;
        if (playbackDonePendingRef.current && vadModeRef.current === "auto" && phaseRef.current === "connected" && !endingRef.current) {
          autoStartMic();
        }
        break;
      case "error_logged":
        setErrors((prev) => [...prev, msg.error]);
        break;
      case "vocab_logged":
        break;
      case "reconnecting":
        // The server is cycling its Gemini connection. `fresh` means native
        // resumption failed and the transcript was re-injected — the risky path
        // for repetition/self-dialogue, so make it visible instead of silent.
        console.warn(`Voice connection reconnecting (attempt ${msg.attempt}${msg.fresh ? ", fresh session" : ""})`);
        if (msg.fresh) setToastError("Connection blip — Elena is picking the conversation back up.");
        break;
      case "level_changed":
        // Elena changed the student's CEFR level via the set_level tool; the new
        // value is already persisted server-side. Nothing to update here — the
        // Settings page re-fetches on mount.
        break;
      case "session_ended":
        if (endTimerRef.current) { clearTimeout(endTimerRef.current); endTimerRef.current = null; }
        forceHideThinking();
        setEnding(false);
        setPhase("ended");
        setSessionEnd({ sessionId: msg.session_id, topic: msg.topic, summary: msg.summary });
        audioWorklet.stop();
        visualiser.reset();
        break;
      case "error":
        forceHideThinking();
        setToastError(msg.message);
        audioWorklet.stop();
        visualiser.reset();
        setPhase("idle");
        setSessionId(null);
        setTranscript([]);
        setErrors([]);
        setSessionEnd(null);
        setEnding(false);
        break;
    }
  }, [audioPlayback, audioWorklet, visualiser, showThinking, hideThinking, forceHideThinking, autoStartMic]);

  const ws = useWebSocket({
    onMessage: handleMessage,
    onOpen: () => {
      setPhase("connected");
      startTimeRef.current = Date.now();
      setElapsedSeconds(0);
      // Fired on WS open, not on the create call: this is the first moment a
      // session is genuinely under way. `session_number` comes from the server —
      // it's what makes the session 1 → session 2 retention funnel possible.
      track("session_started", {
        session_number: sessionNumberRef.current,
        engine: settings?.session_engine ?? "live",
        mode: sessionModeRef.current,
      });
      // Regular (cascaded) engine synthesizes the greeting server-side before any
      // audio/text arrives. Show the processing indicator during that gap so the
      // transcript reads "processing" rather than sitting empty until she speaks.
      // Gated on greeting being enabled so the dots are guaranteed to be cleared
      // by the incoming greeting (first audio/text), never left hanging.
      if (settings?.session_engine === "cascaded" && settings?.elena_greets_first) {
        showThinking();
      }
    },
    onClose: () => {
      if (phase === "connected") audioWorklet.stop();
    },
  });

  useEffect(() => {
    if (phase !== "connected") return;
    const interval = setInterval(() => {
      if (pausedRef.current) return; // counter frozen while paused
      if (startTimeRef.current !== null) {
        setElapsedSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [phase]);

  // Clear the end-session safety timer if the component unmounts mid-teardown.
  useEffect(() => () => {
    if (endTimerRef.current) clearTimeout(endTimerRef.current);
    if (thinkingClearTimerRef.current) clearTimeout(thinkingClearTimerRef.current);
  }, []);

  // Auto-dismiss toast after 6 seconds.
  useEffect(() => {
    if (!toastError) return;
    const t = setTimeout(() => setToastError(null), 6000);
    return () => clearTimeout(t);
  }, [toastError]);

  // Keep sendRef in sync so audioWorklet's onChunk always has the latest send
  sendRef.current = ws.send;

  // A long-lived tab (especially an iOS Safari/PWA tab, which survives for days)
  // keeps running the bundle it loaded even after a deploy replaces the assets on
  // the server — old hashed assets are deleted, so the stale SPA silently runs
  // outdated session code. Compare the bundle hash in the freshly served
  // index.html with the one this page is running; on mismatch, reload to pick up
  // the new build instead of starting a session on dead code.
  const isStaleBuild = async (): Promise<boolean> => {
    try {
      const current = (document.querySelector('script[src*="/assets/index-"]') as HTMLScriptElement | null)?.src;
      if (!current) return false; // dev server — no hashed bundle
      const res = await fetch("/", { cache: "no-store" });
      const served = (await res.text()).match(/\/assets\/index-[\w-]+\.js/)?.[0];
      return !!served && !current.includes(served);
    } catch {
      return false; // offline/unreachable — let the session attempt proceed
    }
  };

  const startSession = async (mode: SessionMode = "normal", focusKey?: string) => {
    // Must run synchronously within the click's call stack so iOS Safari
    // doesn't leave the playback AudioContext permanently suspended.
    audioPlayback.unlock();
    setConnecting(true);
    if (await isStaleBuild()) {
      setToastError("Updating to the latest version…");
      setTimeout(() => window.location.reload(), 800);
      return;
    }
    try {
      const { session_id, session_number, mode: usedMode, focus_label } = await api.createSession(mode, focusKey);
      setSessionId(session_id);
      sessionNumberRef.current = session_number;
      sessionModeRef.current = usedMode ?? "normal";
      setFocusLabel(usedMode === "focus" ? focus_label : null);
      elenaBubbleOpenRef.current = false;
      studentBubbleOpenRef.current = false;
      setTranscript([]);
      setErrors([]);
      setSessionEnd(null);
      ws.connect(session_id);
    } catch (err) {
      // 402 = plan quota reached / trial expired. Surface the server's message and
      // send the user to the upgrade screen (which also offers session packs).
      if (err instanceof ApiError && err.status === 402) {
        setToastError(err.message);
        navigate("/upgrade");
        return;
      }
      setToastError("Couldn't start the session. Please try again.");
      console.error("Failed to create session:", err);
    } finally {
      setConnecting(false);
    }
  };

  const handleToggleMic = () => {
    if (audioWorklet.isRecording || audioWorklet.isArming) {
      // End the turn: flush the last audio, then signal "done". The student's words
      // are now being transcribed (STT) — show the student-side "transcribing" dots.
      audioWorklet.stop();
      elenaBubbleOpenRef.current = false; // turn boundary — see handleSilence
      elenaDoneRef.current = false; // stale elena_done must not re-arm the mic — see handleSilence
      ws.send({ type: "turn_end" });
      showTranscribing();
    } else {
      // Start a turn. Barge-in: cut off any of Elena's playback first.
      audioPlayback.abort();
      elenaBubbleOpenRef.current = false;
      studentBubbleOpenRef.current = false;
      ws.send({ type: "turn_start", mic_rate: audioWorklet.micRateRef.current });
      audioWorklet.start().catch((err) => {
        ws.send({ type: "turn_end" }); // mic failed — don't leave the turn open
        const message = err instanceof Error ? err.message : String(err);
        setTranscript((prev) => [...prev, newBubble("elena", `⚠ Mic error: ${message}`)]);
        console.error("Failed to start mic:", err);
      });
    }
  };

  // Spacebar toggles the mic when connected and not focused inside a text input.
  useEffect(() => {
    if (phase !== "connected") return;
    const handler = (e: KeyboardEvent) => {
      if (e.code !== "Space") return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      e.preventDefault();
      handleToggleMic();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [phase, handleToggleMic]);

  const handleSendText = (text: string) => {
    // A typed message is a complete turn: close both bubbles so Elena's reply
    // starts fresh and later STT never appends to this typed bubble.
    elenaBubbleOpenRef.current = false;
    studentBubbleOpenRef.current = false;
    elenaDoneRef.current = false; // Elena owes a reply — see handleSilence
    setTranscript((prev) => [...prev, newBubble("student", text)]);
    ws.send({ type: "text", content: text });
    showThinking();
  };

  const handleTogglePause = () => {
    if (phase !== "connected" || ending) return;
    if (paused) {
      // Resume: shift the start time forward by the paused span so the frozen
      // seconds don't reappear, then tell the server to unfreeze its time-limit clock.
      if (pauseStartRef.current !== null && startTimeRef.current !== null) {
        startTimeRef.current += Date.now() - pauseStartRef.current;
      }
      pauseStartRef.current = null;
      pausedRef.current = false;
      setPaused(false);
      ws.send({ type: "resume" });
    } else {
      // Pause: stop the mic, silence Elena, freeze the counter, and freeze the
      // server's time-limit clock. Any open voice turn is simply dropped — the
      // student re-taps the mic after resuming.
      pauseStartRef.current = Date.now();
      pausedRef.current = true;
      setPaused(true);
      if (audioWorklet.isRecording) audioWorklet.stop();
      audioPlayback.abort();
      forceHideThinking();
      ws.send({ type: "pause" });
    }
  };

  const handleEnd = () => {
    if (ending || phase !== "connected") return;
    setEnding(true);
    // Close an open voice turn first so the activity stream is clean before end.
    if (audioWorklet.isRecording) {
      audioWorklet.stop();
      ws.send({ type: "turn_end" });
    }
    ws.send({ type: "end_session" });
    // If the server doesn't confirm within 12s (dead WS, hung teardown), close
    // the UI locally so the button never appears to do nothing.
    endTimerRef.current = setTimeout(() => {
      endTimerRef.current = null;
      setEnding(false);
      setPhase("ended");
      setSessionEnd((prev) => prev ?? { sessionId: sessionId ?? 0, topic: null, summary: null });
      visualiser.reset();
    }, 12000);
  };

  const handleNewSession = () => {
    if (endTimerRef.current) { clearTimeout(endTimerRef.current); endTimerRef.current = null; }
    ws.disconnect();
    forceHideThinking();
    pauseStartRef.current = null;
    pausedRef.current = false;
    setPaused(false);
    setEnding(false);
    setPhase("idle");
    setSessionId(null);
    setTranscript([]);
    setErrors([]);
    setSessionEnd(null);
    visualiser.reset();
  };

  const panelProps = {
    phase,
    isRecording: audioWorklet.isRecording,
    ending,
    barHeights: visualiser.heights,
    elapsedSeconds,
    errors,
    settings,
    focusLabel,
  };

  return (
    <div className="flex flex-1 overflow-hidden relative">
      {toastError && (
        <div className="fixed top-6 right-6 z-50 flex items-start gap-3 bg-surface border border-red-500/40 text-red-400 text-sm rounded-xl px-4 py-3 shadow-xl max-w-sm">
          <span className="mt-px">⚠</span>
          <span className="flex-1 leading-snug">{toastError}</span>
          <button onClick={() => setToastError(null)} className="text-text-tertiary hover:text-text-primary ml-1 shrink-0">✕</button>
        </div>
      )}
      <TutorPanel {...panelProps} />

      <div className="flex-1 flex flex-col min-w-0">
        {phase !== "idle" && <TutorStrip {...panelProps} />}
        {phase === "idle" ? (
          <IdleHero
            onStart={startSession}
            onShowFocus={() => setSearchParams({ focus: "1" })}
            settings={settings}
            connecting={connecting}
            focusTargets={focusTargets}
            preferFocus={preferFocus}
            initialFocusKey={searchParams.get("target")}
          />
        ) : (
          <div className="flex-1 flex flex-col min-h-0 relative">
            <Transcript entries={transcript} thinking={elenaThinking} transcribing={transcribing || audioWorklet.isRecording || audioWorklet.isArming} />
            {paused && (
              <div className="absolute inset-0 bg-surface/40 backdrop-blur-[1px] flex items-center justify-center z-10 pointer-events-none animate-fade-in">
                <div className="flex items-center gap-2 bg-surface2/80 border border-warning/30 rounded-full px-4 py-2 shadow-lg">
                  <span className="w-2 h-2 rounded-full bg-warning animate-pulse-soft" />
                  <span className="text-[0.8125rem] font-medium text-warning">Paused</span>
                </div>
              </div>
            )}
          </div>
        )}
        {phase === "connected" && (
          <AudioControls
            isRecording={audioWorklet.isRecording}
            arming={audioWorklet.isArming}
            ending={ending}
            paused={paused}
            vadMode={vadMode}
            allowAuto={!IS_TOUCH_DEVICE}
            onToggleVadMode={() => setVadMode((m) => m === "manual" ? "auto" : "manual")}
            onToggleMic={handleToggleMic}
            onSendText={handleSendText}
            onTogglePause={handleTogglePause}
            onEnd={handleEnd}
          />
        )}
      </div>

      {sessionEnd && (
        <SessionOverlay
          sessionId={sessionEnd.sessionId}
          topic={sessionEnd.topic}
          summary={sessionEnd.summary}
          durationSeconds={elapsedSeconds}
          correctionCount={errors.length}
          onNewSession={handleNewSession}
        />
      )}
    </div>
  );
}
